import { uploadDocumentImage, generateProjectDocumentPdf } from "./api/ops";
import { uploadSingleFileAsync, base64ToBytes } from "./api/drive";
import { submitAbschlussbericht, upsertAbschlussberichtDraft } from "./api/abschlussbericht";
import { upsertProjektdokumentation, updateProjektdokumentationStatus as apiUpdateProjektdokumentationStatus } from "./api/projektdokumentation";
import { generateUniqueImageName } from "./imageService";
import localforage from "localforage";
import { useDocumentStore } from "../store/documentState";
import type { QueueItem, QueuedUploadFile, ProcessResult, DocumentUploadQueueData, HubspotTextPropertyQueueData } from '../interfaces/queue';
import { useDealsStore } from "../store/dealsStore";
import { getSignedUrlAsync as getSignedUrl, updateTextPropertyAsync as updateDealTextProperty } from "./api/hubspot";
import { DOCUMENT_TO_PROPERTY_MAP, getDocumentTypeConfigById } from "./hubspotDealMapper";
import { AbschlussberichtDraft, AbschlussberichtSubmission } from "../types/abschlussbericht";
import { resolvePhaseFolderName } from "../utils/phaseFolder";
import { UploadedDocumentFile } from "../types/documents";
import type { ProjectStatusSnapshot } from "../types/hubspot";
import {
  normalizeUploadedDocumentFiles,
  uploadedDocumentFilesToPropertyValue,
} from "../utils/uploadedDocumentFiles";
import {
  ProjektdokumentationFormData,
  ProjektdokumentationStatusUpdate,
} from "../types/projektdokumentation";

// Upload-Queue-Service für Offline-Funktionalität

// IndexedDB Store konfigurieren
const queueStore = localforage.createInstance({
  name: "360volt-queue",
  storeName: "upload-queue",
});

const notifyProjectStatusRefresh = (successfulIds: string[]) => {
  if (typeof window === "undefined" || successfulIds.length === 0) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent("project-status-refresh", {
      detail: { successfulIds },
    })
  );
};

/**
 * Timeout-Helper: bricht den übergebenen AbortController bei Ablauf ab, damit der
 * zugrunde liegende Request wirklich abgebrochen wird (statt nur weiterzulaufen,
 * während das Item schon für einen Retry neu eingereiht wird → doppelte Uploads).
 */
const timeoutPromise = (ms: number, controller: AbortController): Promise<never> => {
  return new Promise((_, reject) => {
    setTimeout(() => {
      controller.abort();
      reject(new Error(`Timeout after ${ms}ms`));
    }, ms);
  });
};

/**
 * Queue aus IndexedDB laden
 */
export const loadQueue = async (): Promise<QueueItem[]> => {
  try {
    // Alle Queue-Items aus IndexedDB parallel laden statt sequenziell.
    const keys = await queueStore.keys();
    const items = await Promise.all(keys.map((key) => queueStore.getItem<QueueItem>(key)));

    return items.filter((item): item is QueueItem => item != null);
  } catch (error) {
    console.error("Fehler beim Laden der Queue aus IndexedDB:", error);
    return [];
  }
};

/**
 * Queue-Item zur IndexedDB hinzufügen
 */
export const addQueueItem = async (item: QueueItem): Promise<void> => {
  try {
    await queueStore.setItem(item.id, item);
  } catch (error) {
    console.error("Fehler beim Speichern des Queue-Items in IndexedDB:", error);
  }
};

/**
 * Queue-Item aus IndexedDB entfernen
 */
export const removeQueueItem = async (id: string): Promise<void> => {
  try {
    await queueStore.removeItem(id);
  } catch (error) {
    console.error(
      "Fehler beim Entfernen des Queue-Items aus IndexedDB:",
      error
    );
  }
};

/**
 * Queue-Items verarbeiten mit Timeout pro Item
 */
export const processQueue = async (
  items: QueueItem[]
): Promise<ProcessResult> => {
  const successful: string[] = [];
  const failed: string[] = [];

  // Items, die das Retry-Limit erreicht haben, werden nicht mehr automatisch verarbeitet,
  // zählen aber als "failed" (statt spurlos aus successful/failed zu verschwinden) — der
  // User kann sie über den manuellen Sync (setzt retries zurück) erneut versuchen.
  const validItems = items.filter((item) => item.retries < 5);
  const exhaustedItems = items.filter((item) => item.retries >= 5);
  exhaustedItems.forEach((item) => failed.push(item.id));

  // Keine Items zu verarbeiten
  if (validItems.length === 0) {
    return { successful, failed };
  }

  // Offline-Erkennung
  if (!navigator.onLine) {
    validItems.forEach((item) => failed.push(item.id));
    return { successful, failed };
  }

  // Items mit Promise.allSettled + Timeout verarbeiten
  const results = await Promise.allSettled(
    validItems.map(async (item) => {
      const controller = new AbortController();
      try {
        // 30s Timeout pro Upload — bricht bei Ablauf den zugrunde liegenden Request
        // via AbortController ab, statt ihn im Hintergrund weiterlaufen zu lassen.
        await Promise.race([
          processQueueItem(item, controller.signal),
          timeoutPromise(30000, controller),
        ]);
        return { success: true, id: item.id };
      } catch (error) {
        console.error(`Fehler/Timeout bei Queue-Item ${item.id}:`, error);
        const msg = error instanceof Error ? error.message : String(error);
        if (/403|session denied/i.test(msg)) {
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        // Fehlendes Auth-Token ist ein Client-seitiges Timing-Problem (z.B. Token
        // beim Kaltstart noch nicht geladen), keine echte Server-Ablehnung — muss
        // wie ein transienter Fehler retried werden, nicht permanent verworfen.
        const isAuthMissing = (error as any)?.isAuthMissing === true;
        // 4xx-Fehler (außer isAuthMissing) sind permanent — kein Retry, Item sofort entfernen
        const status = (error as any)?.status;
        if (!isAuthMissing && typeof status === 'number' && status >= 400 && status < 500) {
          console.warn(`Queue-Item ${item.id} mit Status ${status} dauerhaft verworfen (kein Retry bei 4xx)`);
          await removeQueueItem(item.id);
          return { success: false, id: item.id };
        }
        // Transiente Fehler (Netzwerk, Auth-Timing, 5xx, Timeout): Retry-Counter erhöhen.
        // Bekannte Ineffizienz: IndexedDB speichert pro Item ein Gesamtobjekt, daher wird
        // beim Retry der komplette Payload (inkl. evtl. eingebetteter Base64-Bilddaten) neu
        // geschrieben, nur um den Zähler zu erhöhen. Ein echter Fix würde große Datei-Payloads
        // in einen separaten IndexedDB-Store auslagern (getrennt von den kleinen Metadaten) —
        // das ist eine Schema-Änderung und bewusst nicht Teil dieses Fixes.
        const updatedItem = { ...item, retries: item.retries + 1 };
        await addQueueItem(updatedItem);
        return { success: false, id: item.id };
      }
    })
  );

  // Ergebnisse auswerten
  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    const item = validItems[i];

    if (result.status === "fulfilled" && result.value.success) {
      successful.push(item.id);
      // Erfolgreiche Items aus IndexedDB entfernen
      await removeQueueItem(item.id);
    } else {
      failed.push(item.id);
    }
  }

  // Background Sync registrieren, wenn Browser es unterstützt
  if ("serviceWorker" in navigator && "SyncManager" in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register("syncQueue");
    } catch (error) {
      console.error("Background Sync konnte nicht registriert werden:", error);
    }
  }

  notifyProjectStatusRefresh(successful);

  return { successful, failed };
};

/**
 * Einzelnes Queue-Item verarbeiten (intern)
 */
async function processQueueItem(item: QueueItem, signal?: AbortSignal): Promise<void> {
  switch (item.type) {
    case "image_upload":
      await processImageUpload(item, signal);
      break;

    case "document_upload":
      // Dokument-Upload (z.B. von PhaseScreen)
      await processDocumentUpload(item, signal);
      break;

    case "make_pdf_upload":
      await processMakePdfUpload(item);
      break;

    case "abschlussbericht_submit":
      await processAbschlussberichtSubmit(item);
      break;

    case "abschlussbericht_draft_upsert":
      await processAbschlussberichtDraftUpsert(item);
      break;

    case "projektdokumentation_upsert":
      await processProjektdokumentationUpsert(item);
      break;

    case "projektdokumentation_status_update":
      await processProjektdokumentationStatusUpdate(item);
      break;

    case "hubspot_text_property_update":
      await processHubspotTextPropertyUpdate(item);
      break;

    default:
      throw new Error(`Unbekannter Queue-Item-Typ: ${item.type}`);
  }
}

/**
 * Formular-PDF verarbeiten
 *
 * Kontext:
 * - Das Formular wird im Modal ausgefüllt.
 * - Wenn offline: HTML wird in IndexedDB gequeued.
 * - Wenn online: Dieser Worker ruft die serverseitige PDF-Erzeugung auf und speichert fileId im Store.
 */
async function processMakePdfUpload(item: QueueItem): Promise<void> {
  const {
    dealId,
    documentTitle,
    htmlContent,
    filename,
    phaseId,
    documentId,
    pdfFields,
    signatures,
    signatureMissing,
    isPdfForm,
    formDataInputs,
  } = item.data || {};

  if (!filename || !documentTitle) {
    throw new Error("Unvollständige Make-PDF-Daten");
  }
  if (!dealId || !documentId) {
    throw new Error("dealId oder documentId fehlen für PDF-Erzeugung");
  }
  if (isPdfForm && !pdfFields) {
    throw new Error("VDZ: pdf_fields fehlen");
  }
  if (!isPdfForm && !htmlContent) {
    throw new Error("HTML Route: htmlContent fehlt");
  }

  // formDataInputs kommt als JSON-String an (siehe FormularModal.extractFormData) –
  // das Backend erwartet ein JSON-Objekt (JsonElement), daher parsen. Ein Parse-Fehler
  // muss den Upload fehlschlagen lassen (Retry über die Queue), sonst geht der
  // Formularstand für HubSpot stillschweigend verloren, obwohl das PDF erzeugt wurde.
  let parsedFormDataInputs: unknown;
  if (typeof formDataInputs === "string" && formDataInputs.trim()) {
    try {
      parsedFormDataInputs = JSON.parse(formDataInputs);
    } catch (e) {
      throw new Error(`formDataInputs konnte nicht geparst werden: ${e instanceof Error ? e.message : String(e)}`);
    }
  } else if (formDataInputs) {
    parsedFormDataInputs = formDataInputs;
  }

  const result = await generateProjectDocumentPdf(dealId, documentId, {
    documentTitle,
    fileName: filename,
    htmlContent: htmlContent || "",
    isPdfForm: isPdfForm ?? false,
    signatureMissing: signatureMissing ?? false,
    ...(isPdfForm && { pdfFields, signatures: signatures || {} }),
    ...(parsedFormDataInputs !== undefined && { formDataInputs: parsedFormDataInputs }),
  });

  const fileId = result?.fileId;
  if (!fileId) {
    throw new Error("PDF-Erzeugung lieferte keine fileId");
  }

  // Store-Update, damit die UI sofort "Anzeigen" anzeigen kann.
  if (phaseId && documentId) {
    const { saveDocumentFileId, updateDocumentStatus, saveDocumentData } =
      useDocumentStore.getState();

    saveDocumentFileId(phaseId, documentId, fileId);
    updateDocumentStatus(phaseId, documentId, "completed");

    // Optional: Signed URL direkt cachen (bessere UX beim "Anzeigen")
    try {
      const url = await getSignedUrl(fileId);
      if (url) saveDocumentData(phaseId, documentId, url);
    } catch (e) {
      console.warn("[queueService] Konnte Signed URL nicht cachen:", e);
    }
  }
}

/**
 * Hilfsfunktion: Dateiendung aus Data-URL ermitteln
 */
const getExtensionFromDataUrl = (dataUrl: string): string => {
  const mime = dataUrl.match(/:(.*?);/)?.[1];
  if (mime === "application/pdf") return ".pdf";
  if (mime === "image/png") return ".png";
  if (mime === "image/webp") return ".webp";
  if (mime === "image/heic") return ".heic";
  if (mime === "image/heif") return ".heif";
  return ".jpg"; // Default
};

const getExtensionFromFileName = (fileName?: string): string | null => {
  const match = fileName?.trim().match(/(\.[a-z0-9]{2,5})$/i);
  return match?.[1]?.toLowerCase() || null;
};

const getUploadFileName = (file: QueuedUploadFile): string => {
  const extension = getExtensionFromFileName(file.name) || getExtensionFromDataUrl(file.dataUrl);
  return generateUniqueImageName().replace(".jpg", extension);
};

const getDocumentUploadFiles = (item: QueueItem): QueuedUploadFile[] => {
  const files = Array.isArray(item.data?.files)
    ? item.data.files
        .filter((file: any) => typeof file?.dataUrl === "string" && file.dataUrl.trim().length > 0)
        .map((file: any) => ({
          id: file.id,
          name: file.name,
          type: file.type || file.mimeType,
          size: file.size,
          dataUrl: file.dataUrl,
        }))
    : [];

  if (files.length > 0) return files;
  if (typeof item.data?.imageData === "string" && item.data.imageData.trim().length > 0) {
    return [{ dataUrl: item.data.imageData }];
  }
  return [];
};

const appendUploadedDocumentFile = (
  phaseId: string,
  documentId: string,
  file: UploadedDocumentFile
): UploadedDocumentFile[] => {
  const { appendDocumentFiles } = useDocumentStore.getState();
  return appendDocumentFiles(phaseId, documentId, [file]);
};

/**
 * Bild-Upload verarbeiten – bevorzugt Google Drive, Fallback HubSpot
 */
async function processImageUpload(item: QueueItem, signal?: AbortSignal): Promise<void> {
  const { imageData, checklistItemId, phaseId, dealName, sharedDrive, gdriveFolderId } = item.data;

  const extension = getExtensionFromDataUrl(imageData);
  const fileName = generateUniqueImageName().replace(".jpg", extension);

  let fileId: string | null | void = null;

  if (dealName) {
    // Google Drive Upload: Ordnerstruktur = dealName / Dokumente / Phasenname
    const phaseDisplayName = resolvePhaseFolderName(phaseId);
    const driveResult = await uploadSingleFileAsync(
      imageData,
      fileName,
      dealName,
      ['Dokumente', phaseDisplayName],
      { sharedDrive, gdriveFolderId },
      signal,
    );
    if (driveResult) {
      const { saveDocumentFileId, saveDocumentData } = useDocumentStore.getState();
      saveDocumentFileId(phaseId, checklistItemId, driveResult.fileId);
      saveDocumentData(phaseId, checklistItemId, driveResult.webViewLink);
    }
    return;
  } else {
    // Fallback: HubSpot via Backend (wenn kein dealName übergeben)
    fileId = await uploadDocumentImage(phaseId, checklistItemId, imageData, fileName, signal);
  }

  if (fileId) {
    const { saveDocumentFileId } = useDocumentStore.getState();
    saveDocumentFileId(phaseId, checklistItemId, fileId);
    console.log(`[queueService] Drive FileId gespeichert: ${fileId} für ${checklistItemId}`);
  }
}

/**
 * Dokument-Upload verarbeiten (von PhaseScreen) – bevorzugt Google Drive, Fallback HubSpot
 */
async function processDocumentUpload(item: QueueItem, signal?: AbortSignal): Promise<void> {
  const { documentId, phaseId, dealName, sharedDrive, gdriveFolderId }: DocumentUploadQueueData = item.data;
  const uploadFiles = getDocumentUploadFiles(item);

  if (uploadFiles.length === 0 || !documentId || !phaseId) {
    throw new Error("Unvollständige Dokument-Upload-Daten");
  }

  const existingFiles = normalizeUploadedDocumentFiles(item.data?.existingFiles);
  if (existingFiles.length > 0) {
    const { appendDocumentFiles } = useDocumentStore.getState();
    appendDocumentFiles(phaseId, documentId, existingFiles);
  }

  const docConfig = getDocumentTypeConfigById(documentId);

  for (const uploadFile of uploadFiles) {
    const fileName = getUploadFileName(uploadFile);
    let fileId: string | null | void = null;

    if (dealName) {
      // Google Drive Upload: Ordnerstruktur = dealName / Dokumente / Phasenname
      const phaseDisplayName = resolvePhaseFolderName(phaseId);
      const driveResult = await uploadSingleFileAsync(
        uploadFile.dataUrl,
        fileName,
        dealName,
        ['Dokumente', phaseDisplayName],
        { sharedDrive, gdriveFolderId },
        signal,
      );
      if (driveResult) {
        const { saveDocumentData, updateDocumentStatus } = useDocumentStore.getState();
        const mergedFiles = appendUploadedDocumentFile(phaseId, documentId, {
          id: driveResult.fileId,
          fileId: driveResult.fileId,
          url: driveResult.webViewLink,
          name: uploadFile.name || fileName,
          mimeType: uploadFile.type,
          uploadedAt: new Date().toISOString(),
          thumbnailUrl: driveResult.thumbnailLink,
          previewData: uploadFile.dataUrl,
          source: "drive",
        });
        saveDocumentData(phaseId, documentId, driveResult.webViewLink);
        updateDocumentStatus(phaseId, documentId, "completed");

        // Drive-Link nach HubSpot patchen (außer Dokumenttypen mit eigenem Flow, z.B. Seriennummer)
        if (!docConfig?.skipHubspotPatch) {
          const dealId = phaseId.split('-')[0];
          const hubspotProperty = DOCUMENT_TO_PROPERTY_MAP[documentId];
          if (dealId && hubspotProperty && driveResult.webViewLink) {
            // Multi-Upload-Dokumente patchen ein JSON-Array aller Dateien, Einzel-Upload-
            // Dokumente patchen nur den Drive-Link — gesteuert über die Dokumenttyp-
            // Konfiguration statt über hartkodierte Phasen-/ID-Sonderfälle.
            const value = docConfig?.multiUpload
              ? uploadedDocumentFilesToPropertyValue(mergedFiles)
              : driveResult.webViewLink;
            try {
              await updateDealTextProperty(dealId, hubspotProperty, value);
            } catch (e) {
              // Der Drive-Upload ist bereits erfolgreich und darf beim Retry nicht erneut
              // hochgeladen werden — statt den Fehler zu verschlucken (Datei landet in
              // Drive, aber nie im HubSpot-Deal), wird der Property-Patch als eigenes,
              // unabhängig retry-fähiges Queue-Item eingereiht.
              console.warn('[queueService] HubSpot Drive-Link Patch fehlgeschlagen, wird als eigenes Retry-Item eingereiht:', e);
              await addQueueItem({
                id: `hubspot-patch-retry-${dealId}-${hubspotProperty}-${Date.now()}`,
                type: "hubspot_text_property_update",
                data: { dealId, property: hubspotProperty, value },
                retries: 0,
                createdAt: new Date(),
              });
            }
          }
        }
      }
    } else {
      // Fallback: HubSpot via Backend
      fileId = await uploadDocumentImage(phaseId, documentId, uploadFile.dataUrl, fileName, signal);

      if (fileId) {
        const { updateDocumentStatus } = useDocumentStore.getState();
        const uploadedFile = normalizeUploadedDocumentFiles(fileId)[0];
        if (uploadedFile) {
          appendUploadedDocumentFile(phaseId, documentId, {
            ...uploadedFile,
            name: uploadFile.name || fileName,
            mimeType: uploadFile.type,
            previewData: uploadFile.dataUrl,
          });
        }
        updateDocumentStatus(phaseId, documentId, "completed");
        console.log(`[queueService] FileId gespeichert: ${fileId} für ${documentId}`);
      }
    }
  }
}

async function processHubspotTextPropertyUpdate(item: QueueItem): Promise<void> {
  const { dealId, property, value }: HubspotTextPropertyQueueData = item.data;

  if (!dealId || !property) {
    throw new Error("Unvollständige HubSpot-Property-Update-Daten");
  }

  await updateDealTextProperty(dealId, property, value);
}

async function processAbschlussberichtSubmit(item: QueueItem): Promise<void> {
  const {
    projectId,
    phaseId,
    submission,
  }: {
    projectId?: string;
    phaseId?: string;
    submission?: AbschlussberichtSubmission;
  } = item.data || {};

  if (!projectId || !phaseId || !submission) {
    throw new Error("Unvollständige Abschlussbericht-Daten");
  }

  await submitAbschlussbericht(projectId, submission);

  const { updatePhaseStatus } = useDocumentStore.getState();
  updatePhaseStatus(phaseId, "completed", submission.submittedAt, submission.submittedByEmail);
}

async function processAbschlussberichtDraftUpsert(item: QueueItem): Promise<void> {
  const {
    projectId,
    draft,
  }: {
    projectId?: string;
    draft?: AbschlussberichtDraft;
  } = item.data || {};

  if (!projectId || !draft) {
    throw new Error("Unvollständige Abschlussbericht-Draft-Daten");
  }

  await upsertAbschlussberichtDraft(projectId, draft);
}

async function processProjektdokumentationUpsert(item: QueueItem): Promise<void> {
  const {
    projectId,
    payload,
  }: {
    projectId?: string;
    payload?: ProjektdokumentationFormData;
  } = item.data || {};

  if (!projectId || !payload) {
    throw new Error("Unvollständige Projektdokumentation-Daten");
  }

  await upsertProjektdokumentation(projectId, payload);
}

async function processProjektdokumentationStatusUpdate(item: QueueItem): Promise<void> {
  const {
    projectId,
    phaseId,
    payload,
  }: {
    projectId?: string;
    phaseId?: string;
    payload?: ProjektdokumentationStatusUpdate;
  } = item.data || {};

  if (!projectId || !payload) {
    throw new Error("Unvollständige Projektdokumentation-Statusdaten");
  }

  const response = await apiUpdateProjektdokumentationStatus(projectId, payload);

  const existingDeal = useDealsStore.getState().deals.find((deal) => deal.id === projectId);
  if (existingDeal) {
    const existingSnapshot = existingDeal.project_status || null;
    // API liefert bei einem Status-Update immer einen vollständigen Snapshot zurück
    // (kein partielles Patch) — die Merge-Reihenfolge stellt sicher, dass bereits
    // vorhandene Felder erhalten bleiben, falls die Response ausnahmsweise unvollständig ist.
    const nextSnapshot = response?.projectStatus
      ? ({
          ...(existingSnapshot || {}),
          ...response.projectStatus,
        } as ProjectStatusSnapshot)
      : existingSnapshot
        ? { ...existingSnapshot }
        : null;
    if (nextSnapshot) {
      nextSnapshot.projektdokumentationApprovalStatus =
        response?.record?.status || nextSnapshot.projektdokumentationApprovalStatus || null;
    }
    useDealsStore.getState().upsertDeal({
      ...existingDeal,
      project_status: nextSnapshot,
    });
  }

  if (payload.status === "approved" && phaseId) {
    const approvedAt = response?.record?.approvedAt || new Date().toISOString();
    useDocumentStore
      .getState()
      .updatePhaseStatus(
        phaseId,
        "completed",
        approvedAt,
        response?.record?.completedByEmail || undefined
      );
  }
}

/**
 * Data-URL in Blob konvertieren
 */
export const dataUrlToBlob = (dataUrl: string): Blob => {
  const [meta, base64] = dataUrl.split(",");
  const mime = meta.match(/:(.*?);/)?.[1];
  return new Blob([base64ToBytes(base64)], { type: mime });
};

/**
 * Sync-State aus localStorage löschen (bei Logout)
 */
export const clearSyncState = (): void => {
  try {
    localStorage.removeItem("sync-state");
    localStorage.removeItem("sync-timestamp");
    localStorage.removeItem("sync-last-result");
    console.log("Sync-State cleared");
  } catch (error) {
    console.error("Fehler beim Löschen des Sync-State:", error);
  }
};
